import { limitCopy, resetPhrase } from '../daily-limit/daily-limit';

/*
 * The "come back tomorrow" block on a new student's first quiz results.
 *
 * Who sees it (exam-result.jsx and use-first-quiz.js hold the rest):
 * - a student, on the web (not the iOS app), right after submitting a quiz
 *   (exam.jsx's submit pushes { quizFinished: true }; a review never submits);
 * - whose account is at most FIRST_QUIZ_MAX_ACCOUNT_AGE_DAYS old, read from
 *   /user/me's createdAt. This keeps the quiz-list read below to new accounts,
 *   whose lists are a few rows, and keeps the reminder email's promise true
 *   (see below);
 * - and for whom this is the first completed quiz: GET /quiz-history/user/{id}
 *   (the Quiz History page's own read) lists this quiz and no other complete one.
 *
 * Why 5 days: the practice email (QLearn-service PracticeEmailRunner) goes out
 * on account days 1, 3 and 6, counted in UTC calendar days from sign-up, and
 * only to accounts that have answered a question. A first quiz on day 5 or
 * earlier still has one of those ahead, so "We'll email you a reminder" holds.
 */
export const FIRST_QUIZ_MAX_ACCOUNT_AGE_DAYS = 5;

const DAY_MS = 24 * 60 * 60 * 1000;
// A device clock a little behind the server can put createdAt in the future.
const CLOCK_SKEW_MS = 10 * 60 * 1000;

const startOfUtcDay = (ms) => {
  const date = new Date(ms);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

/**
 * The account's age in UTC calendar days (0 on the sign-up day), the practice
 * email's own rule. null when createdAt is missing or unparseable: the server
 * leaves it out when it has no verifiable sign-up time, and a stand-in would
 * show the block to accounts of any age.
 */
export const accountAgeDaysUtc = (createdAt, now = new Date()) => {
  const createdMs = typeof createdAt === 'string' ? Date.parse(createdAt) : Number.NaN;
  if (!Number.isFinite(createdMs)) return null;
  const nowMs = now.getTime();
  const days = Math.round((startOfUtcDay(nowMs) - startOfUtcDay(createdMs)) / DAY_MS);
  if (days >= 0) return days;
  return createdMs - nowMs <= CLOCK_SKEW_MS ? 0 : null;
};

export const isNewAccount = (createdAt, now = new Date()) => {
  const age = accountAgeDaysUtc(createdAt, now);
  return age !== null && age <= FIRST_QUIZ_MAX_ACCOUNT_AGE_DAYS;
};

const sameId = (a, b) => typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();

/**
 * Whether `examId` is the student's first completed quiz, from the quiz list
 * (UserQuizHistoryDto rows: id, iscomplete). The page already knows this quiz is
 * COMPLETE; the list must include it (so it is this student's fresh list) and no
 * other row may be complete. In-progress quizzes don't count.
 */
export const isFirstCompletedQuiz = (quizzes, examId) => {
  if (!Array.isArray(quizzes) || typeof examId !== 'string' || !examId) return false;
  const listed = quizzes.some((quiz) => sameId(quiz?.id, examId));
  const otherCompleted = quizzes.some((quiz) => quiz?.iscomplete === true && !sameId(quiz?.id, examId));
  return listed && !otherCompleted;
};

/**
 * When the next set unlocks, for the block. Only a confirmed free plan with a
 * daily allowance gets the clock time (the server's tally restarts at 00:00 UTC,
 * named in the student's own clock by resetPhrase), with the same words as the
 * daily-limit card. Paid and unknown plans get a plain nudge, never a time.
 */
export const unlockLine = (info, { now = new Date(), timeZone, locale } = {}) => {
  if (info?.free === true && Number.isInteger(info.limit) && info.limit > 0) {
    return limitCopy({ limit: info.limit, free: true, exam: info.exam ?? null, when: resetPhrase(now, { timeZone, locale }) }).body;
  }
  return 'Come back tomorrow for more practice.';
};

/*
 * The reminder email ("Remind me"), from GET /qlearn/user/preference/{userId}.
 *
 * - on: emailPracticeQuestions is true (the default for new accounts). The
 *   button only confirms; nothing is saved.
 * - off: the student turned it off AND the server says they did not use "stop
 *   all marketing" (emailMarketingOptOut === false). One tap turns it back on.
 * - hidden: everything else. "Stop all marketing" also turns the practice email
 *   off, and the preference read does not report emailMarketingOptOut today, so
 *   an "off" without an explicit false is never offered a re-enable.
 */
export const REMINDER = Object.freeze({ ON: 'on', OFF: 'off', HIDDEN: 'hidden' });

export const reminderState = (preference) => {
  if (!preference || typeof preference !== 'object') return REMINDER.HIDDEN;
  if (preference.emailMarketingOptOut === true) return REMINDER.HIDDEN;
  if (preference.emailPracticeQuestions === true) return REMINDER.ON;
  if (preference.emailPracticeQuestions === false && preference.emailMarketingOptOut === false) {
    return REMINDER.OFF;
  }
  return REMINDER.HIDDEN;
};
